import React, { PropsWithChildren, ReactNode } from "react";
import {
  Box,
  Button,
  Stack,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { env } from "../../constants";
import { ReactComponent as LockIcon } from "../../assets/amazonAccount/lock.svg";
import { ReactComponent as RevokeIcon } from "../../assets/amazonAccount/revoke.svg";
import { ReactComponent as TickIcon } from "../../assets/amazonAccount/blue-tick.svg";

export const AMAZON_CONNECT_RETURN_PATH_KEY = "amazonConnectReturnPath";

export const DEFAULT_CONNECT_BENEFITS = [
  "Unlock real-time pricing, sales, and competition insights.",
  "Automatically calculate profits with accurate FBA fees.",
  "Save hours of manual research with automated product insights.",
];

/**
 * Seller Central consent URL. Adds redirect_uri when REACT_APP_AMAZON_REDIRECT_URI is set,
 * so each environment (e.g. staging) gets the user back to its own /authorize/amazon page
 * instead of the Amazon app's default redirect.
 */
export const buildAmazonConsentUrl = (
  appId: string = env.AMAZON_APP_ID,
  redirectUri: string | undefined = env.AMAZON_REDIRECT_URI,
) => {
  const params = new URLSearchParams({
    application_id: appId,
    state: "na",
    version: "beta",
  });
  if (redirectUri) params.set("redirect_uri", redirectUri);
  return `https://sellercentral.amazon.com/apps/authorize/consent?${params.toString()}`;
};

/**
 * Opens Amazon's Seller Central consent page in a new tab.
 * When returnPath is given, /authorize-amazon sends the user back there after
 * connecting (the new tab inherits this tab's sessionStorage).
 */
export const openAmazonConsent = (returnPath?: string) => {
  if (returnPath)
    sessionStorage.setItem(AMAZON_CONNECT_RETURN_PATH_KEY, returnPath);
  else sessionStorage.removeItem(AMAZON_CONNECT_RETURN_PATH_KEY);
  window.open(buildAmazonConsentUrl(), "_blank");
};

const BenefitItem: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <SvgIcon component={TickIcon} />
      <Typography fontSize="16px" color="#13191F" fontWeight="600">
        {children}
      </Typography>
    </Stack>
  );
};

type Props = {
  onConnect: () => void;
  subtitle?: string;
  benefits?: string[];
  /** Rendered under the Connect button, e.g. "Remind me later". */
  secondaryAction?: ReactNode;
};

/** The "Connect your Amazon Seller Account" content, shared by the modal and full pages. */
export const SellerCentralConnect: React.FC<Props> = ({
  onConnect,
  subtitle = "(optional but recommended)",
  benefits = DEFAULT_CONNECT_BENEFITS,
  secondaryAction,
}) => {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  return (
    <Stack>
      <Typography
        textAlign="center"
        fontSize="24px"
        fontWeight={600}
        color="#13191F"
      >
        Connect your Amazon Seller Account
      </Typography>
      <Typography
        textAlign="center"
        fontSize="14px"
        color="#485E75"
        fontWeight={400}
      >
        {subtitle}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: smUp ? "row" : "column",
          alignItems: "center",
          border: "1px solid #D9E0E8",
          borderRadius: "6px",
          padding: "12px",
          marginTop: 5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          marginRight={smUp ? "12px" : 0}
          marginBottom={smUp ? 0 : "12px"}
          spacing={1}
        >
          <SvgIcon component={LockIcon} />
          <Typography
            textAlign="left"
            fontSize="14px"
            color="#485E75"
            fontWeight={400}
          >
            Protect your business security and data privacy
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          borderLeft={smUp ? "1px solid #D9E0E8" : "none"}
          borderTop={smUp ? "none" : "1px solid #D9E0E8"}
          paddingLeft={smUp ? "12px" : 0}
          paddingTop={smUp ? 0 : "12px"}
          spacing={1}
        >
          <SvgIcon component={RevokeIcon} />
          <Typography
            textAlign="left"
            fontSize="14px"
            color="#485E75"
            fontWeight={400}
          >
            Revoke access to Seller Central at any time
          </Typography>
        </Stack>
      </Box>

      <Typography
        textAlign="left"
        fontSize="14px"
        color="#485E75"
        fontWeight={400}
        marginTop={5}
      >
        Once connected, you'll be able to:
      </Typography>

      <Stack spacing={2} marginTop={2}>
        {benefits.map((benefit) => (
          <BenefitItem key={benefit}>{benefit}</BenefitItem>
        ))}
      </Stack>

      <Button variant="contained" size="large" sx={{ mt: 4 }} onClick={onConnect}>
        Connect to Seller Central
      </Button>
      {secondaryAction}
    </Stack>
  );
};

export default SellerCentralConnect;
